import {inject, injectable} from 'inversify';
import {makeAutoObservable, reaction, runInAction} from 'mobx';

import {delay} from '@yourcoach/async-utils';

import type {ConferenceExpandedCommon} from '../../api/conference';
import type {WithBusyManager, WithReactionDispose} from '../../utils/store';
import type {Nullable} from '../../utils/utility-types';

import {conferenceExpandCommon, conferenceStore} from '../../api/conference';
import {currentUserStore} from '../../api/user';
import {isIamCoach} from '../../utils/entity/isIamCoach';
import {logger} from '../../utils/logger';
import {AsyncBusyManager, ReactionDisposeManager} from '../../utils/store';
import {FetchMissedCromsCountQuery} from '../ports/MissedCroms/fetchMissedCromsCount/FetchMissedCromsCount.query';

type BusyKey = 'fetch' | 'fetchByUserId';

@injectable()
export class MissedCromsCountStore
  implements WithBusyManager<BusyKey>, WithReactionDispose
{
  busy = new AsyncBusyManager<BusyKey>();
  disposer = new ReactionDisposeManager();
  private countByUserId = new Map<string, number>();
  private total: Nullable<number> = null;

  constructor(
    @inject(FetchMissedCromsCountQuery)
    private readonly fetchMissedCromsCountQuery: FetchMissedCromsCountQuery,
  ) {
    makeAutoObservable(this, undefined, {
      autoBind: true,
    });

    this.busy.register('fetch');
    this.busy.register('fetchByUserId');

    this.fetchTotal();

    this.setupReactions();
  }

  decrementCount(userId: string) {
    const count = this.countByUserId.get(userId) ?? 0;

    if (count > 0) {
      this.countByUserId.set(userId, count - 1);
      this.total = Math.max(0, (this.total ?? 0) - 1);

      this.fetchTotal();
      this.fetchCountByUserId(userId);
    }
  }

  getCountByUserId(userId: string) {
    const count = this.countByUserId.get(userId);

    if (count === undefined) {
      this.fetchCountByUserId(userId);

      return 0;
    }

    return count;
  }

  getTotal() {
    if (this.total === null) {
      this.fetchTotal();

      runInAction(() => {
        this.total = 0;
      });

      return 0;
    }

    return this.total;
  }

  incrementCount(userId: string) {
    const count = this.countByUserId.get(userId) ?? 0;

    this.countByUserId.set(userId, count + 1);
    this.total = (this.total ?? 0) + 1;

    this.fetchTotal();
    this.fetchCountByUserId(userId);
  }

  private async fetchCountByUserId(
    userId: string,
    {isSilent}: {isSilent?: boolean} = {},
  ) {
    if (this.busy.getReason('fetchByUserId') || !this.isNeedToFetch()) {
      return;
    }

    try {
      if (!isSilent) {
        this.busy.setReason('fetchByUserId', true);
      }

      const count = await this.fetchMissedCromsCountQuery.fetchCount({
        userId,
      });

      runInAction(() => {
        this.countByUserId.set(userId, count);
      });
    } catch (error) {
      logger.error(error);
    } finally {
      this.busy.setReason('fetchByUserId', false);
    }
  }

  private async fetchTotal({isSilent}: {isSilent?: boolean} = {}) {
    if (this.busy.getReason('fetch') || !this.isNeedToFetch()) {
      return;
    }

    try {
      if (!isSilent) {
        this.busy.setReason('fetch', true);
      }

      const total = await this.fetchMissedCromsCountQuery.fetchCount();

      runInAction(() => {
        this.total = total;
      });
    } catch (error) {
      logger.error(error);
    } finally {
      this.busy.setReason('fetch', false);
    }
  }

  private isNeedToFetch() {
    return isIamCoach();
  }

  private async processConference(conferenceId: string) {
    const conference = (await conferenceStore.coach.fetch({
      _id: conferenceId,
      expand: conferenceExpandCommon,
    })) as ConferenceExpandedCommon;

    const clientId = conference.course?.client_id;

    if (clientId) {
      const currentTotal = this.total ?? 0;

      let attempts = 0;

      while (attempts < 5 && this.total === currentTotal) {
        await this.fetchTotal({isSilent: true});
        attempts++;

        await delay(2000);
      }

      await this.fetchCountByUserId(clientId, {isSilent: true});
    }
  }

  private reset() {
    this.total = null;
    this.countByUserId.clear();

    this.fetchTotal();
  }

  private setupReactions() {
    this.disposer.watch(
      'conference.stopping',
      reaction(
        () => conferenceStore.coach.stopping,
        stopping => {
          if (stopping.success) {
            const conference = stopping.entity;

            if (conference) {
              this.processConference(conference._id);
            }
          }
        },
      ),
    );

    this.disposer.watch(
      'user',
      reaction(
        () => currentUserStore.user,
        () => this.reset(),
      ),
    );
  }
}
